import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Modal, Form, Alert, Row, Col, Card, Badge, Spinner, Tab, Tabs } from "react-bootstrap";
import { Link } from "react-router-dom";
import PermissionWrapper from "../components/PermissionWrapper";
import { IMAGE_BASE } from "../Config";
import { apiGet, apiPost, apiPut, apiDelete } from "../services/BaseService";

interface Program {
  _id: string;
  title: string;
  programCode: string;
  description: string;
  programType: string;
  language: string;
  durationHours: number;
  durationMinutes: number;
  email: string;
  phone: string;
  image1: string;
  image2: string;
  image3: string;
  isFeatured: boolean;
}

interface ProgramType {
  _id: string;
  name: string;
}

interface Group {
  _id: string;
  name: string;
  country: string;
  state: string;
}

interface Country {
  countryCode: string;
  name: string;
}

interface State {
  stateCode: string;
  name: string;
}

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'hi', name: 'Hindi' },
  { code: 'bn', name: 'Bengali' },
  { code: 'te', name: 'Telugu' },
  { code: 'mr', name: 'Marathi' },
  { code: 'ta', name: 'Tamil' },
  { code: 'gu', name: 'Gujarati' },
  { code: 'kn', name: 'Kannada' },
  { code: 'ml', name: 'Malayalam' },
  { code: 'pa', name: 'Punjabi' },
  { code: 'or', name: 'Odia' },
  { code: 'as', name: 'Assamese' }
];

const COUNTRY_NAMES: Record<string, string> = {
  "IN": "India",
  "US": "United States", 
  "UK": "United Kingdom",
  "CA": "Canada",
  "AU": "Australia"
};

const STATE_NAMES: Record<string, string> = {
  "MH": "Maharashtra",
  "KA": "Karnataka", 
  "TN": "Tamil Nadu",
  "DL": "Delhi",
  "GJ": "Gujarat",
  "RJ": "Rajasthan",
  "UP": "Uttar Pradesh",
  "WB": "West Bengal"
};

const ManageAllProgramsPage: React.FC = () => {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingProgram, setEditingProgram] = useState<Program | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Search and filter states
  const [searchText, setSearchText] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState("");

  // Form data
  const [formData, setFormData] = useState({
    title: "",
    programCode: "",
    description: "",
    programType: "",
    language: "",
    durationHours: 0,
    durationMinutes: 0,
    email: "",
    phone: "",
    image1: "",
    image2: "",
    image3: "",
    isFeatured: false
  });

  // Reference data
  const [programTypes, setProgramTypes] = useState<ProgramType[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);

  // File uploads
  const [imageFiles, setImageFiles] = useState<{
    image1: File | null;
    image2: File | null; 
    image3: File | null;
  }>({
    image1: null,
    image2: null,
    image3: null
  });

  // Festival play data
  const [availablePlays, setAvailablePlays] = useState<any[]>([]);
  const [festivalPlays, setFestivalPlays] = useState<any[]>([]);
  const [festivalForm, setFestivalForm] = useState({
    playId: "",
    scheduledDate: "",
    scheduledTime: "",
    venue: "",
    notes: ""
  });

  // Group search in modal
  const [groupSearch, setGroupSearch] = useState({
    name: "",
    country: "",
    state: ""
  });

  const generateProgramCode = useCallback(() => {
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `PROG-${timestamp}-${random}`;
  }, []);

  const loadPrograms = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '100',
        excludeTypes: '6839c953077dbaaa4e2581da' // Exclude Theater Play type
      });
      
      if (searchText) params.append('search', searchText);
      if (selectedCountry) params.append('country', selectedCountry);
      if (selectedState) params.append('state', selectedState);
      if (selectedGroupId) params.append('groupId', selectedGroupId);
      
      const response = await apiGet(`/programs/exclude-types?${params.toString()}`);
      setPrograms(response.programs || []);
    } catch (err) {
      setError("Failed to load programs");
      setPrograms([]);
    } finally {
      setLoading(false);
    }
  }, [searchText, selectedCountry, selectedState, selectedGroupId]);

  const loadProgramTypes = useCallback(async () => {
    try {
      const data = await apiGet('/programtypes');
      setProgramTypes(data.filter((type: ProgramType) => type._id !== "6839c953077dbaaa4e2581da"));
    } catch (err) {
      setProgramTypes([]);
    }
  }, []);

  const loadCountries = useCallback(async () => {
    try {
      const data = await apiGet('/groups/distinct/countries');
      setCountries(data.map((code: string) => ({ 
        countryCode: code, 
        name: COUNTRY_NAMES[code] || code 
      })));
    } catch (err) {
      setCountries([]);
    }
  }, []);

  const loadStates = useCallback(async (countryCode: string) => {
    if (!countryCode) {
      setStates([]);
      return;
    }
    try {
      const data = await apiGet(`/groups/distinct/states/${countryCode}`);
      setStates(data.map((code: string) => ({ 
        stateCode: code, 
        name: STATE_NAMES[code] || code 
      })));
    } catch (err) {
      setStates([]);
    }
  }, []);

  const loadGroups = useCallback(async () => {
    if (!selectedCountry && !selectedState) {
      setGroups([]);
      return;
    }
    
    try {
      const params = new URLSearchParams();
      if (selectedCountry) params.append('country', selectedCountry);
      if (selectedState) params.append('state', selectedState);
      
      const response = await apiGet(`/groups/filter?${params.toString()}`);
      setGroups(response.groups || []);
    } catch (err) {
      setGroups([]);
    }
  }, [selectedCountry, selectedState]);

  const loadModalGroups = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (groupSearch.country) params.append('country', groupSearch.country);
      if (groupSearch.state) params.append('state', groupSearch.state);
      
      const url = params.toString() ? `/groups/filter?${params.toString()}` : '/groups';
      const response = await apiGet(url);
      return response.groups || response || [];
    } catch (err) {
      return [];
    }
  }, [groupSearch.country, groupSearch.state]);

  const loadAvailablePlays = useCallback(async () => {
    try {
      const data = await apiGet('/festival-plays/available-plays');
      setAvailablePlays(data);
    } catch (err) {
      setAvailablePlays([]);
    }
  }, []);

  const loadFestivalPlays = useCallback(async (festivalId: string) => {
    try {
      const data = await apiGet(`/festival-plays/festival/${festivalId}`);
      setFestivalPlays(data);
    } catch (err) {
      setFestivalPlays([]);
    }
  }, []);

  const loadProgramGroups = useCallback(async (programId: string) => {
    try {
      const data = await apiGet(`/programgroups/program/${programId}`);
      setSelectedGroups(data.map((pg: any) => pg.groupId));
    } catch (err) {
      setSelectedGroups([]);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadPrograms();
    loadProgramTypes();
    loadCountries();
  }, [loadPrograms, loadProgramTypes, loadCountries]);

  // Load states when country changes
  useEffect(() => {
    loadStates(selectedCountry);
    setSelectedState("");
    setSelectedGroupId("");
  }, [selectedCountry, loadStates]);

  // Load groups when country or state changes
  useEffect(() => {
    loadGroups();
    setSelectedGroupId("");
  }, [selectedCountry, selectedState, loadGroups]);

  const handleCountryChange = (country: string) => {
    setSelectedCountry(country);
  };

  const handleStateChange = (state: string) => {
    setSelectedState(state);
  };

  const handleSearch = () => {
    loadPrograms();
  };

  const openModal = async (program?: Program) => {
    if (program) {
      setEditingProgram(program);
      setFormData({
        title: program.title,
        programCode: program.programCode,
        description: program.description,
        programType: program.programType,
        language: program.language,
        durationHours: program.durationHours,
        durationMinutes: program.durationMinutes,
        email: program.email,
        phone: program.phone,
        image1: program.image1,
        image2: program.image2,
        image3: program.image3,
        isFeatured: program.isFeatured
      });
      await loadProgramGroups(program._id);
      
      const type = programTypes.find(t => t._id === program.programType);
      if (type?.name.toLowerCase().includes('festival')) {
        await loadAvailablePlays();
        await loadFestivalPlays(program._id);
      }
    } else {
      setEditingProgram(null);
      setFormData({
        title: "",
        programCode: generateProgramCode(),
        description: "",
        programType: "",
        language: "",
        durationHours: 0,
        durationMinutes: 0,
        email: "",
        phone: "",
        image1: "",
        image2: "",
        image3: "",
        isFeatured: false
      });
      setSelectedGroups([]);
      setFestivalPlays([]);
      setFestivalForm({
        playId: "",
        scheduledDate: "",
        scheduledTime: "",
        venue: "",
        notes: ""
      });
    }
    
    setImageFiles({ image1: null, image2: null, image3: null });
    setShowModal(true);
    setError(null);
    setSuccess(null);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProgram(null);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleFileChange = (imageKey: keyof typeof imageFiles) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setImageFiles(prev => ({ ...prev, [imageKey]: e.target.files![0] }));
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    const result = await apiPost('/image/upload/theaterPlayImage', formData);
    return `/theaterPlayImage/${result.fileName}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.programType) {
      setError("Please select a program type");
      return;
    }
    
    setUploading(true);
    try {
      // Upload new images
      const imageUploads = await Promise.all([
        imageFiles.image1 ? uploadImage(imageFiles.image1) : formData.image1,
        imageFiles.image2 ? uploadImage(imageFiles.image2) : formData.image2,
        imageFiles.image3 ? uploadImage(imageFiles.image3) : formData.image3
      ]);

      const submitData = {
        ...formData,
        image1: imageUploads[0] || "",
        image2: imageUploads[1] || "",
        image3: imageUploads[2] || ""
      };

      const savedProgram = editingProgram 
        ? await apiPut(`/programs/${editingProgram._id}`, submitData)
        : await apiPost('/programs', submitData);
      
      // Save program groups
      if (selectedGroups.length > 0) {
        await Promise.all(selectedGroups.map(groupId => 
          apiPost('/programgroups', { programId: savedProgram._id, groupId })
        ));
      }

      setSuccess(editingProgram ? "Program updated successfully" : "Program created successfully");
      await loadPrograms();
      closeModal();
    } catch (err) {
      setError(editingProgram ? "Failed to update program" : "Failed to create program");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (programId: string) => {
    if (!window.confirm("Are you sure you want to delete this program?")) return;
    
    try {
      await apiDelete(`/programs/${programId}`);
      setSuccess("Program deleted successfully");
      await loadPrograms();
    } catch (err) {
      setError("Failed to delete program");
    }
  };

  const handleFestivalFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFestivalForm(prev => ({ ...prev, [name]: value }));
  };

  const addFestivalPlay = async () => {
    if (!editingProgram || !festivalForm.playId || !festivalForm.scheduledDate || !festivalForm.scheduledTime) {
      setError("Please fill all required fields");
      return;
    }

    try {
      await apiPost('/festival-plays', {
        festivalId: editingProgram._id,
        ...festivalForm
      });
      
      setSuccess("Play added to festival successfully");
      await loadFestivalPlays(editingProgram._id);
      setFestivalForm({
        playId: "",
        scheduledDate: "",
        scheduledTime: "",
        venue: "",
        notes: ""
      });
      setError(null);
    } catch (err) {
      setError("Failed to add play to festival");
    }
  };

  const removeFestivalPlay = async (festivalPlayId: string) => {
    if (!window.confirm("Are you sure you want to remove this play from the festival?")) return;
    
    try {
      await apiDelete(`/festival-plays/${festivalPlayId}`);
      setSuccess("Play removed from festival successfully");
      if (editingProgram) {
        await loadFestivalPlays(editingProgram._id);
      }
    } catch (err) {
      setError("Failed to remove play from festival");
    }
  };

  const formatDuration = (hours: number, minutes: number) => {
    if (!hours && !minutes) return "Duration TBA";
    const h = hours ? `${hours}h` : "";
    const m = minutes ? `${minutes}m` : "";
    return `${h} ${m}`.trim();
  };

  const getProgramTypeName = (programTypeId: string) => {
    const type = programTypes.find(t => t._id === programTypeId);
    return type?.name || "Unknown Type";
  };

  const isTheaterFestival = (programTypeId: string) => {
    const type = programTypes.find(t => t._id === programTypeId);
    return type?.name.toLowerCase().includes('festival') || false;
  };

  // Memoized filtered groups for modal
  const modalGroups = useMemo(() => {
    return groups.filter(group => {
      if (groupSearch.name && !group.name.toLowerCase().includes(groupSearch.name.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [groups, groupSearch.name]);

  return (
    <PermissionWrapper pageName="ManageAllProgramsPage">
      <div>
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <h3>
                <i className="bi bi-collection me-2"></i>
                Manage All Programs
              </h3>
              <Button variant="primary" onClick={() => openModal()}>
                <i className="bi bi-plus-circle me-2"></i>
                Add Program
              </Button>
            </div>
          </Col>
        </Row>

        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0">Search & Filter</h6>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col md={3}>
                <Form.Select value={selectedCountry} onChange={(e) => handleCountryChange(e.target.value)}>
                  <option value="">All Countries</option>
                  {countries.map(country => (
                    <option key={country.countryCode} value={country.countryCode}>
                      {country.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select 
                  value={selectedState} 
                  onChange={(e) => handleStateChange(e.target.value)} 
                  disabled={!selectedCountry}
                >
                  <option value="">All States</option>
                  {states.map(state => (
                    <option key={state.stateCode} value={state.stateCode}>
                      {state.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select 
                  value={selectedGroupId} 
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                  disabled={!selectedCountry}
                >
                  <option value="">All Groups</option>
                  {groups.map(group => (
                    <option key={group._id} value={group._id}>
                      {group.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <div className="d-flex">
                  <Form.Control
                    type="text"
                    placeholder="Search programs..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="me-2"
                  />
                  <Button variant="outline-secondary" onClick={handleSearch}>
                    <i className="bi bi-search"></i>
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

        <div className="mb-3">
          <small className="text-muted">
            {programs.length > 0 ? `Showing ${programs.length} programs` : "No programs found"}
          </small>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <Row className="g-3">
            {programs.map((program) => (
              <Col xs={12} sm={6} md={4} lg={3} key={program._id}>
                <Card className="h-100">
                  <div style={{ height: '200px', overflow: 'hidden' }}>
                    {program.image1 ? (
                      <Card.Img 
                        variant="top" 
                        src={IMAGE_BASE + program.image1}
                        style={{ height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/default-profile-image.jpg';
                        }}
                      />
                    ) : (
                      <div className="d-flex align-items-center justify-content-center h-100 bg-light">
                        <i className="bi bi-collection display-4 text-muted"></i>
                      </div>
                    )}
                  </div>
                  <Card.Body className="d-flex flex-column">
                    <Card.Title className="h6">{program.title}</Card.Title>
                    <Badge bg="info" className="mb-2 align-self-start">
                      {getProgramTypeName(program.programType)}
                    </Badge>
                    <Card.Text className="small text-muted flex-grow-1">
                      {program.description ? 
                        (program.description.length > 80 ? 
                          program.description.substring(0, 80) + '...' : 
                          program.description
                        ) : 'No description available'
                      }
                    </Card.Text>
                    <div className="mb-2">
                      <div className="small text-muted">
                        <i className="bi bi-clock me-1"></i>
                        {formatDuration(program.durationHours, program.durationMinutes)}
                      </div>
                      {program.language && (
                        <div className="small text-muted">
                          <i className="bi bi-translate me-1"></i>
                          {LANGUAGES.find(l => l.code === program.language)?.name || program.language}
                        </div>
                      )}
                    </div>
                    <div className="d-flex gap-1 mt-auto">
                      <Link
                        to={`/program/${program._id}`}
                        className="btn btn-outline-warning btn-sm flex-fill"
                      >
                        <i className="bi bi-eye me-1"></i>
                        View
                      </Link>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => openModal(program)}
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(program._id)}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        <Modal show={showModal} onHide={closeModal} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>{editingProgram ? "Edit Program" : "Add Program"}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Tabs defaultActiveKey="basic">
                <Tab eventKey="basic" title="Basic Info">
                  <Row className="mt-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Program Title</Form.Label>
                        <Form.Control
                          type="text"
                          name="title"
                          value={formData.title}
                          onChange={handleFormChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Program Code</Form.Label>
                        <div className="d-flex">
                          <Form.Control
                            type="text"
                            name="programCode"
                            value={formData.programCode}
                            onChange={handleFormChange}
                            required
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={() => setFormData(prev => ({ ...prev, programCode: generateProgramCode() }))}
                            className="ms-2"
                            type="button"
                          >
                            <i className="bi bi-arrow-clockwise"></i>
                          </Button>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Program Type <span className="text-danger">*</span></Form.Label>
                        <Form.Select
                          name="programType"
                          value={formData.programType}
                          onChange={handleFormChange}
                          required
                        >
                          <option value="">Select Program Type</option>
                          {programTypes.map(type => (
                            <option key={type._id} value={type._id}>
                              {type.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Language</Form.Label>
                        <Form.Select
                          name="language"
                          value={formData.language}
                          onChange={handleFormChange}
                        >
                          <option value="">Select Language</option>
                          {LANGUAGES.map(lang => (
                            <option key={lang.code} value={lang.code}>
                              {lang.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Duration Hours</Form.Label>
                        <Form.Control
                          type="number"
                          name="durationHours"
                          value={formData.durationHours}
                          onChange={handleFormChange}
                          min="0"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Duration Minutes</Form.Label>
                        <Form.Control
                          type="number"
                          name="durationMinutes"
                          value={formData.durationMinutes}
                          onChange={handleFormChange}
                          min="0"
                          max="59"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleFormChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleFormChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="description"
                      value={formData.description}
                      onChange={handleFormChange}
                    />
                  </Form.Group>

                  {editingProgram && (
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        id="featured-switch"
                        label="Featured Program"
                        name="isFeatured"
                        checked={formData.isFeatured}
                        onChange={handleFormChange}
                      />
                    </Form.Group>
                  )}
                </Tab>

                <Tab eventKey="images" title="Images">
                  <Row className="mt-3">
                    {(['image1', 'image2', 'image3'] as const).map((imageKey, index) => (
                      <Col md={4} key={imageKey}>
                        <Form.Group className="mb-3">
                          <Form.Label>Image {index + 1}</Form.Label>
                          <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange(imageKey)}
                          />
                          {formData[imageKey] && (
                            <div className="mt-2">
                              <img 
                                src={IMAGE_BASE + formData[imageKey]}
                                alt={`Image ${index + 1}`}
                                style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                className="rounded"
                              />
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                    ))}
                  </Row>
                </Tab>

                {editingProgram && isTheaterFestival(editingProgram.programType) && (
                  <Tab eventKey="festival-plays" title="Festival Plays">
                    <div className="mt-3 mb-4">
                      <h6>Add Theater Play to Festival</h6>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Select Play <span className="text-danger">*</span></Form.Label>
                            <Form.Select
                              name="playId"
                              value={festivalForm.playId}
                              onChange={handleFestivalFormChange}
                            >
                              <option value="">Select a theater play</option>
                              {availablePlays.map(play => (
                                <option key={play._id} value={play._id}>
                                  {play.title} ({play.programCode})
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Scheduled Date <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                              type="date"
                              name="scheduledDate"
                              value={festivalForm.scheduledDate}
                              onChange={handleFestivalFormChange}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Scheduled Time <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                              type="time"
                              name="scheduledTime"
                              value={festivalForm.scheduledTime}
                              onChange={handleFestivalFormChange}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Venue</Form.Label>
                            <Form.Control
                              type="text"
                              name="venue"
                              value={festivalForm.venue}
                              onChange={handleFestivalFormChange}
                              placeholder="Enter venue name"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Form.Group className="mb-3">
                        <Form.Label>Notes</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          name="notes"
                          value={festivalForm.notes}
                          onChange={handleFestivalFormChange}
                          placeholder="Additional notes"
                        />
                      </Form.Group>
                      <Button variant="primary" onClick={addFestivalPlay} type="button">
                        <i className="bi bi-plus-circle me-2"></i>
                        Add Play to Festival
                      </Button>
                    </div>

                    <div>
                      <h6>Scheduled Plays ({festivalPlays.length})</h6>
                      {festivalPlays.length === 0 ? (
                        <p className="text-muted">No plays scheduled for this festival yet.</p>
                      ) : (
                        <Row>
                          {festivalPlays.map((festivalPlay) => (
                            <Col md={6} key={festivalPlay._id} className="mb-3">
                              <Card>
                                <Card.Body>
                                  <div className="d-flex justify-content-between align-items-start mb-2">
                                    <Card.Title className="h6">{festivalPlay.playId.title}</Card.Title>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => removeFestivalPlay(festivalPlay._id)}
                                      type="button"
                                    >
                                      <i className="bi bi-trash"></i>
                                    </Button>
                                  </div>
                                  <div className="small text-muted">
                                    <div><strong>Date:</strong> {new Date(festivalPlay.scheduledDate).toLocaleDateString()}</div>
                                    <div><strong>Time:</strong> {festivalPlay.scheduledTime}</div>
                                    {festivalPlay.venue && <div><strong>Venue:</strong> {festivalPlay.venue}</div>}
                                    {festivalPlay.notes && <div><strong>Notes:</strong> {festivalPlay.notes}</div>}
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      )}
                    </div>
                  </Tab>
                )}

                <Tab eventKey="groups" title="Groups">
                  <div className="mt-3 mb-3">
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-2">
                          <Form.Label>Search by Name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter group name..."
                            value={groupSearch.name}
                            onChange={(e) => setGroupSearch(prev => ({ ...prev, name: e.target.value }))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-2">
                          <Form.Label>Country</Form.Label>
                          <Form.Select
                            value={groupSearch.country}
                            onChange={(e) => {
                              const country = e.target.value;
                              setGroupSearch(prev => ({ ...prev, country, state: "" }));
                              if (country) {
                                loadStates(country);
                              }
                            }}
                          >
                            <option value="">All Countries</option>
                            {countries.map(country => (
                              <option key={country.countryCode} value={country.countryCode}>
                                {country.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-2">
                          <Form.Label>State</Form.Label>
                          <Form.Select
                            value={groupSearch.state}
                            onChange={(e) => setGroupSearch(prev => ({ ...prev, state: e.target.value }))}
                            disabled={!groupSearch.country}
                          >
                            <option value="">All States</option>
                            {states.map(state => (
                              <option key={state.stateCode} value={state.stateCode}>
                                {state.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Select Groups ({modalGroups.length} found)</Form.Label>
                    <div className="row">
                      {modalGroups.map(group => (
                        <div key={group._id} className="col-md-4 col-sm-6 mb-2">
                          <Form.Check
                            type="checkbox"
                            id={`group-${group._id}`}
                            label={`${group.name} (${group.state || 'N/A'})`}
                            checked={selectedGroups.includes(group._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedGroups(prev => [...prev, group._id]);
                              } else {
                                setSelectedGroups(prev => prev.filter(id => id !== group._id));
                              }
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    {modalGroups.length === 0 && (
                      <p className="text-muted">No groups available. Please select country and state first.</p>
                    )}
                  </Form.Group>
                </Tab>
              </Tabs>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={closeModal} type="button">
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={uploading}>
                {uploading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Uploading...
                  </>
                ) : (
                  <>{editingProgram ? "Update" : "Create"} Program</>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    </PermissionWrapper>
  );
};

export default ManageAllProgramsPage;